import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Slider,
  Grid,
  MenuItem,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemButton,
} from '@mui/material';
import {
  Person,
  Email,
  Lock,
  MusicNote,
  Work,
  AttachMoney,
  CloudUpload,
  Check,
  ArrowBack,
  ArrowForward,
  Language,
  LinkedIn,
  Instagram,
  Facebook,
  Twitter,
  YouTube,
  AudioFile,
  EmojiEvents,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const steps = ['Basic Info', 'Professional Profile', 'Skills & Rates', 'Portfolio & Links'];

// Audio engineering specializations and genres
const SPECIALIZATIONS = [
  'Recording Engineer',
  'Mixing Engineer', 
  'Mastering Engineer',
  'Live Sound Engineer',
  'Studio Engineer',
  'Assistant Engineer',
  'Audio Editor',
  'Sound Designer',
  'Producer',
  'Assistant Producer',
  'Music Producer',
  'Podcast Producer',
  'Post-Production Audio',
  'Broadcast Engineer',
  'Audio Programmer',
];

const GENRES = [
  'Rock', 'Pop', 'Hip Hop', 'R&B', 'Jazz', 'Classical', 'Electronic', 'EDM',
  'Country', 'Folk', 'Blues', 'Reggae', 'Punk', 'Metal', 'Alternative',
  'Indie', 'World Music', 'Latin', 'Gospel', 'Soundtrack', 'Ambient',
  'Experimental', 'Podcast', 'Voiceover', 'Audiobook', 'Sound Design',
];

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner (0-2 years)', description: 'New to professional audio' },
  { value: 'intermediate', label: 'Intermediate (2-5 years)', description: 'Some professional experience' },
  { value: 'experienced', label: 'Experienced (5-10 years)', description: 'Established professional' },
  { value: 'expert', label: 'Expert (10+ years)', description: 'Industry veteran' },
];

// Validation schemas for each step
const basicInfoSchema = yup.object({
  firstName: yup
    .string()
    .required('First name is required')
    .min(1, 'First name must be at least 1 character')
    .max(100, 'First name must be less than 100 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(1, 'Last name must be at least 1 character')
    .max(100, 'Last name must be less than 100 characters'),
  email: yup
    .string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
});

const professionalProfileSchema = yup.object({
  displayName: yup
    .string()
    .required('Display name is required')
    .min(2, 'Display name must be at least 2 characters')
    .max(100, 'Display name must be less than 100 characters'),
  bio: yup
    .string()
    .max(1000, 'Bio must be less than 1000 characters'),
  location: yup
    .string()
    .max(100, 'Location must be less than 100 characters'),
  experienceLevel: yup
    .string()
    .required('Please select your experience level')
    .oneOf(['beginner', 'intermediate', 'experienced', 'expert']),
});

const skillsRatesSchema = yup.object({
  specializations: yup
    .array()
    .min(1, 'Please select at least one specialization')
    .max(5, 'Please select no more than 5 specializations'),
  genres: yup
    .array()
    .min(1, 'Please select at least one genre')
    .max(10, 'Please select no more than 10 genres'),
  hourlyRate: yup
    .number()
    .min(0, 'Rate must be positive')
    .max(1000, 'Rate must be reasonable'),
  projectRate: yup
    .number()
    .min(0, 'Rate must be positive')
    .max(50000, 'Rate must be reasonable'),
  availability: yup
    .string()
    .required('Please select your availability'),
});

interface EngineerSignupFormData {
  // Basic info
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  
  // Professional profile
  displayName: string;
  bio?: string;
  location?: string;
  experienceLevel: 'beginner' | 'intermediate' | 'experienced' | 'expert';
  
  // Skills & rates
  specializations: string[];
  genres: string[];
  hourlyRate?: number;
  projectRate?: number;
  availability: 'full-time' | 'part-time' | 'project-based' | 'weekends-only';
  
  // Portfolio & links
  website?: string;
  socialMedia?: {
    linkedin?: string;
    instagram?: string;
    twitter?: string;
    youtube?: string;
  };
  portfolioFiles?: File[];
}

export const IndividualEngineerSignup: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Form setup for current step
  const getSchemaForStep = (step: number) => {
    switch (step) {
      case 0:
        return basicInfoSchema;
      case 1:
        return professionalProfileSchema;
      case 2:
        return skillsRatesSchema;
      default:
        return yup.object({});
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    trigger,
    getValues,
  } = useForm<EngineerSignupFormData>({
    resolver: yupResolver(getSchemaForStep(activeStep)),
    mode: 'onChange',
    defaultValues: {
      specializations: [],
      genres: [],
      socialMedia: {},
    },
  });

  const watchedValues = watch();

  const handleNext = async () => {
    const isValid = await trigger();
    if (!isValid) return;

    if (activeStep === steps.length - 1) {
      // Final step - submit the form
      await handleSubmitForm();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      // If on first step, go back to login page
      navigate('/login');
    } else {
      // Otherwise, go to previous step
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleSubmitForm = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const formData = getValues();
      
      const signupData = {
        ...formData,
        accountType: 'individual_engineer',
      };

      console.log('Engineer signup data:', signupData);
      
      // Call backend API for engineer signup
      const response = await api.post('/engineer-signup/create', signupData);
      
      console.log('Engineer signup response:', response.data);
      
      // Navigate to success page
      navigate('/engineer-signup/success');
    } catch (error: any) {
      console.error('Engineer signup error:', error);
      setError(error.response?.data?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSpecializationToggle = (specialization: string) => {
    const current = watchedValues.specializations || [];
    if (current.includes(specialization)) {
      setValue('specializations', current.filter(s => s !== specialization));
    } else if (current.length < 5) {
      setValue('specializations', [...current, specialization]);
    }
  };

  const handleGenreToggle = (genre: string) => {
    const current = watchedValues.genres || [];
    if (current.includes(genre)) {
      setValue('genres', current.filter(g => g !== genre));
    } else if (current.length < 10) {
      setValue('genres', [...current, genre]);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Create your engineer account
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  margin="normal"
                  {...register('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  margin="normal"
                  {...register('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              margin="normal"
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              margin="normal"
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              margin="normal"
              {...register('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Tell us about yourself
            </Typography>
            
            <TextField
              fullWidth
              label="Display Name"
              margin="normal"
              {...register('displayName')}
              error={!!errors.displayName}
              helperText={errors.displayName?.message || 'This is how other users will see you'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Location (Optional)"
              margin="normal"
              {...register('location')}
              error={!!errors.location}
              helperText={errors.location?.message || 'City, State/Country'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Language />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Professional Bio (Optional)"
              margin="normal"
              multiline
              rows={4}
              {...register('bio')}
              error={!!errors.bio}
              helperText={errors.bio?.message || 'Tell potential clients about your experience and expertise'}
            />

            <FormControl fullWidth margin="normal" error={!!errors.experienceLevel}>
              <FormLabel>Experience Level</FormLabel>
              <RadioGroup
                value={watchedValues.experienceLevel || ''}
                onChange={(e) => setValue('experienceLevel', e.target.value as any)}
              >
                {EXPERIENCE_LEVELS.map((level) => (
                  <FormControlLabel
                    key={level.value}
                    value={level.value}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight="bold">
                          {level.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {level.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
              {errors.experienceLevel && (
                <Typography variant="caption" color="error">
                  {errors.experienceLevel.message}
                </Typography>
              )}
            </FormControl>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Skills & Rates
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Specializations ({(watchedValues.specializations || []).length}/5)
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select up to 5 areas of expertise
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {SPECIALIZATIONS.map((spec) => (
                <Chip
                  key={spec}
                  label={spec}
                  onClick={() => handleSpecializationToggle(spec)}
                  color={(watchedValues.specializations || []).includes(spec) ? 'primary' : 'default'}
                  variant={(watchedValues.specializations || []).includes(spec) ? 'filled' : 'outlined'}
                  clickable
                />
              ))}
            </Box>
            
            {errors.specializations && (
              <Typography variant="caption" color="error" display="block" sx={{ mb: 2 }}>
                {errors.specializations.message}
              </Typography>
            )}

            <Typography variant="h6" gutterBottom>
              Genres ({(watchedValues.genres || []).length}/10)
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select up to 10 genres you work with
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {GENRES.map((genre) => (
                <Chip
                  key={genre}
                  label={genre}
                  onClick={() => handleGenreToggle(genre)}
                  color={(watchedValues.genres || []).includes(genre) ? 'secondary' : 'default'}
                  variant={(watchedValues.genres || []).includes(genre) ? 'filled' : 'outlined'}
                  clickable
                  size="small"
                />
              ))}
            </Box>
            
            {errors.genres && (
              <Typography variant="caption" color="error" display="block" sx={{ mb: 2 }}>
                {errors.genres.message}
              </Typography>
            )}

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Rates (Optional)
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Hourly Rate (USD)"
                  type="number"
                  margin="normal"
                  {...register('hourlyRate')}
                  error={!!errors.hourlyRate}
                  helperText={errors.hourlyRate?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Project Rate (USD)"
                  type="number"
                  margin="normal"
                  {...register('projectRate')}
                  error={!!errors.projectRate}
                  helperText={errors.projectRate?.message || 'Typical project rate'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <FormControl fullWidth margin="normal">
              <FormLabel>Availability</FormLabel>
              <Select
                value={watchedValues.availability || ''}
                onChange={(e) => setValue('availability', e.target.value as any)}
                input={<OutlinedInput />}
              >
                <MenuItem value="full-time">Full-time (40+ hours/week)</MenuItem>
                <MenuItem value="part-time">Part-time (20-40 hours/week)</MenuItem>
                <MenuItem value="project-based">Project-based (as needed)</MenuItem>
                <MenuItem value="weekends-only">Weekends only</MenuItem>
              </Select>
              {errors.availability && (
                <Typography variant="caption" color="error">
                  {errors.availability.message}
                </Typography>
              )}
            </FormControl>
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Portfolio & Social Links
            </Typography>
            
            <TextField
              fullWidth
              label="Website (Optional)"
              margin="normal"
              {...register('website')}
              helperText="Your personal or professional website"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Language />
                  </InputAdornment>
                ),
              }}
            />

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Social Media (Optional)
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="LinkedIn"
                  margin="normal"
                  {...register('socialMedia.linkedin')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkedIn />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Instagram"
                  margin="normal"
                  {...register('socialMedia.instagram')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Instagram />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Twitter"
                  margin="normal"
                  {...register('socialMedia.twitter')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Twitter />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="YouTube"
                  margin="normal"
                  {...register('socialMedia.youtube')}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <YouTube />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Paper sx={{ p: 3, mt: 3, bgcolor: 'background.default', textAlign: 'center' }}>
              <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Portfolio Files (Coming Soon)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload audio samples, demo reels, and previous work to showcase your skills.
                This feature will be available after account creation.
              </Typography>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0A0E1A',
        backgroundImage: `
          radial-gradient(ellipse at top right, rgba(255, 107, 53, 0.1) 0%, transparent 50%),
          radial-gradient(ellipse at bottom left, rgba(142, 36, 170, 0.1) 0%, transparent 50%)
        `,
        padding: 2,
      }}
    >
      <Card sx={{ 
        maxWidth: 800, 
        width: '100%',
        background: 'rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              mb: 3,
              padding: 2,
              borderRadius: '50%',
              background: 'rgba(255, 107, 53, 0.1)',
              border: '2px solid rgba(255, 107, 53, 0.3)',
            }}>
              <MusicNote sx={{ fontSize: 40, color: 'primary.main' }} />
            </Box>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
              Join as an Engineer
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create your professional audio engineer profile
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(handleNext)}>
            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ mr: 1 }}
              >
                {activeStep === 0 ? 'Back to Login' : 'Back'}
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                endIcon={activeStep === steps.length - 1 ? <EmojiEvents /> : <ArrowForward />}
                sx={{
                  background: activeStep === steps.length - 1 
                    ? 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)'
                    : 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                  boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 24px rgba(255, 107, 53, 0.4)',
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : activeStep === steps.length - 1 ? (
                  'Create Account'
                ) : (
                  'Next'
                )}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};